import { Form, Radio, Space } from "antd"
import React, { useState } from "react"
import { Button, DashboardContainer } from "components"
import FormSectionWrapper from "../FormSectionWrapper"
import { AntDesignInput } from "components/antd/AntDesignInput"
import TextArea from "antd/es/input/TextArea"
import { AntDesignRadio } from "components/antd/AntDesignRadio"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { useAppNotificationStore } from "stores"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import { CustomSelect } from "components/new-stuff/form"
import {
  CUSTOM_FIELDS_ENDPOINTS,
  customFieldsService,
} from "services/customFieldsService"
import { onQueryError } from "utils/utils"

const NewCustomField: React.FC<{}> = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()
  const [form] = Form.useForm()
  const ResponseType = Form.useWatch("response", form)
  const group = Form.useWatch("group", form)

  const { isLoading: isGroupLoading, data: groupsRes } = useQuery<
    IBaseRes<ICustomGroup[]>
  >({
    queryKey: [CUSTOM_FIELDS_ENDPOINTS.GET_GROUPS],
    queryFn: async () => customFieldsService.getGroups(),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
    keepPreviousData: true,
  })
  const groups = groupsRes?.data

  const { isLoading: isLoadingCheck, mutate: check } = useMutation({
    mutationFn: async (body: string) => customFieldsService.check(body),
    onSuccess: (data: IBaseRes<any>) => {
      if (data.success) {
        data?.message && toast.success(data?.message)
      }
    },
    onError: onQueryError,
  })
  const { isLoading: isLoadingCheckGroup, mutate: checkGroup } = useMutation({
    mutationFn: async (body: string) => customFieldsService.checkGroup(body),
    onSuccess: (data: IBaseRes<any>) => {
      if (data.success) {
        toast.success(data?.message)
      }
    },
    onError: onQueryError,
  })
  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [CUSTOM_FIELDS_ENDPOINTS.SELECT],
    })
  }

  const { isLoading: isLoading, mutate: submitCustom } = useMutation({
    mutationFn: async (body: Record<string, any>) =>
      customFieldsService.add(body),
    onSuccess: (data: IBaseRes<any>) => {
      if (data.success) {
        mixPanelEvent("settings-customization-custom-fields-add-success")
        data.message && toast.success(data.message)
        navigate("/dashboard/settings/customization/custom-fields")
        invalidateQueries()
      }
    },
    onError: onQueryError,
  })
  const onFinish = (values: any) => {
    console.log(values)

    const { group, name, helperText, group_name, who, response, options } =
      values

    const body = {
      description: name,
      alias: helperText,
      custom_field_group_id: group,
      custom_field_group: group_name || "",
      response_type_id: response,
      response_options: options || "",
      is_admin: who,
    }
    console.log(body)

    mixPanelEvent("settings-customization-custom-fields-add-init")
    submitCustom(body)
  }
  return (
    <DashboardContainer>
      <FormSectionWrapper
        name=" Create New Custom Field"
        title=" Create New Custom Field"
        subtitle="Add a custom field to store any info you like on your employees’ profiles."
        isActive={true}
        layout="vertical"
        form={form}
        onFinish={onFinish}
        style={{ maxWidth: 1024 }}
        autoComplete="off"
        footerBtns={
          <div className="flex justify-end items-center gap-4">
            <Button
              color="secondary"
              type="button"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              title="save"
              submitting={isLoading}
            >
              Save
            </Button>
          </div>
        }
      >
        <div className=" lg:grid lg:grid-cols-2 gap-x-10  px-4 md:px-6 xl:px-[60px]">
          <div className={`${group == 0 ? " col-span-1" : " col-span-2"}`}>
            <CustomSelect
              label="Group"
              name="group"
              required
              isLoading={isGroupLoading}
              options={
                groups?.map((item) => ({
                  label: item.description,
                  value: String(item.customFieldGroupId),
                })) || []
              }
              placeholder="Please select"
              addBottomSpace
              // required
            />
          </div>
          {group == 0 && (
            <div>
              <AntDesignInput
                label="New group"
                name="group_name"
                required={true}
                placeholder="Ex. General Information"
                onBlur={(e) => {
                  checkGroup(e.target.value)
                }}
              />
            </div>
          )}
          <div className="col-span-2">
            <AntDesignInput
              label="Name"
              name="name"
              required={true}
              placeholder="Ex. Genotype"
              onBlur={(e) => {
                check(e.target.value)
              }}
            />
          </div>
          <Form.Item
            required
            className="col-span-2"
            name="helperText"
            label={
              <span className="font-circular text-neutral300 text-sm ">
                Helper text
              </span>
            }
          >
            <TextArea
              rows={3}
              placeholder=""
            />
          </Form.Item>

          <AntDesignRadio
            label="Who should fill out this field?"
            required
            name="who"
          >
            <Space direction="vertical">
              <Radio value="yes"> Admins </Radio>
              <Radio value="no"> Employees </Radio>
            </Space>
          </AntDesignRadio>
          <AntDesignRadio
            label="How should this field be filled?"
            required
            name="response"
          >
            <Space direction="vertical">
              <Radio value="1"> Dropdown </Radio>
              <Radio value="2"> Textbox </Radio>
              <Radio value="3"> File upload </Radio>
            </Space>
          </AntDesignRadio>

          {ResponseType == 1 && (
            <Form.Item
              required
              className="col-span-2"
              name="options"
              label={
                <span className="font-circular text-neutral300 text-sm ">
                  Options
                </span>
              }
            >
              <TextArea
                rows={3}
                placeholder="Separate options with a semicolon(;). Example: Yes;No"
              />
            </Form.Item>
          )}
        </div>
      </FormSectionWrapper>
    </DashboardContainer>
  )
}
export default NewCustomField

export interface ICustomGroup {
  customFieldGroupId: number
  description: string
  createdDate: null
  createdBy: null
  updatedDate: null
  updatedBy: null
  recordGuid: null
  recordVersion: null
  isDeleted: boolean | null
  isActive: boolean | null
  createdByName: null
  updatedByName: null
  companyId: null
  company: null
  customFields: any[]
}
